import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import '../library.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookReader() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [page, setPage] = useState(1);
  const [imgLoading, setImgLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const preloaded = useRef(new Set());

  useEffect(() => {
    axios.get(`${API_URL}/library/books/${bookId}`)
      .then((res) => setBook(res.data))
      .catch(() => setNotFound(true));
    axios.post(`${API_URL}/library/books/${bookId}/read`).catch(() => {});
  }, [bookId]);

  const goTo = useCallback((p) => {
    if (!book) return;
    const next = Math.min(Math.max(1, p), book.pages);
    setPage((prev) => {
      if (next !== prev) setImgLoading(true);
      return next;
    });
  }, [book]);

  // Клавиатура
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goTo(page + 1);
      if (e.key === 'ArrowLeft') goTo(page - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [page, goTo]);

  // Предварително зареждане на съседните страници
  useEffect(() => {
    if (!book) return;
    [page + 1, page + 2, page - 1].forEach((p) => {
      if (p >= 1 && p <= book.pages && !preloaded.current.has(p)) {
        preloaded.current.add(p);
        const img = new Image();
        img.src = `${API_URL}/library/books/${bookId}/page/${p}`;
      }
    });
  }, [page, book, bookId]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#221c14] flex flex-col items-center justify-center text-amber-100">
        <p className="library-font text-lg">Книгата не е намерена</p>
        <Button onClick={() => navigate('/library')} className="mt-4 bg-[#00966E] hover:bg-[#007a59] rounded-full">
          Към библиотеката
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="book-reader" className="h-screen flex flex-col bg-[#221c14]">
      {/* Горна лента */}
      <header className="flex items-center justify-between px-3 sm:px-6 h-14 bg-[#2c241a] border-b border-[#4a3f30] shrink-0">
        <button
          data-testid="reader-back-btn"
          onClick={() => navigate('/library')}
          className="inline-flex items-center gap-2 text-amber-100/90 hover:text-amber-300 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Библиотека</span>
        </button>
        <h1 className="library-font text-amber-50 text-sm sm:text-base font-semibold truncate px-3 text-center">
          {book?.title || '…'}
        </h1>
        <button
          data-testid="reader-download-btn"
          onClick={() => window.open(`${API_URL}/library/books/${bookId}/download`, '_blank')}
          className="inline-flex items-center gap-2 text-amber-100/90 hover:text-amber-300 text-sm font-medium transition-colors"
          title="Свали PDF"
        >
          <Download className="h-4 w-4" />
          <span className="hidden sm:inline">Свали</span>
        </button>
      </header>

      {/* Страница */}
      <main className="flex-1 relative flex items-center justify-center overflow-hidden px-2">
        {/* Стрелки отстрани */}
        <button
          data-testid="reader-prev-btn"
          onClick={() => goTo(page - 1)}
          disabled={page <= 1}
          className="absolute left-1 sm:left-4 z-10 p-2 sm:p-3 rounded-full bg-black/30 text-amber-100 hover:bg-black/50 disabled:opacity-20 transition-colors"
          aria-label="Предишна страница"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        {imgLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-0">
            <Loader2 className="h-10 w-10 text-amber-400 animate-spin" />
          </div>
        )}

        {book && (
          <img
            data-testid="reader-page-img"
            src={`${API_URL}/library/books/${bookId}/page/${page}`}
            alt={`Страница ${page}`}
            onLoad={() => setImgLoading(false)}
            className={`reader-page-img max-h-full max-w-full object-contain transition-opacity duration-200 ${imgLoading ? 'opacity-30' : 'opacity-100'}`}
          />
        )}

        <button
          data-testid="reader-next-btn"
          onClick={() => goTo(page + 1)}
          disabled={!book || page >= book.pages}
          className="absolute right-1 sm:right-4 z-10 p-2 sm:p-3 rounded-full bg-black/30 text-amber-100 hover:bg-black/50 disabled:opacity-20 transition-colors"
          aria-label="Следваща страница"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </main>

      {/* Долна лента */}
      <footer className="px-4 sm:px-8 py-3 bg-[#2c241a] border-t border-[#4a3f30] shrink-0">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <input
            data-testid="reader-page-slider"
            type="range"
            min={1}
            max={book?.pages || 1}
            value={page}
            onChange={(e) => goTo(Number(e.target.value))}
            className="reader-slider flex-1"
          />
          <span data-testid="reader-page-indicator" className="text-amber-100 text-sm font-medium whitespace-nowrap library-font">
            {page} / {book?.pages || '…'}
          </span>
        </div>
      </footer>
    </div>
  );
}
